import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Box, Typography, useTheme } from '@mui/material';
import { isDefined } from 'lib/typeInference';
import { useCurrencyString } from 'lib/useCurrencyString';
import { PAY_MAX_WIDTH } from 'pages/pay';
import { ErrorCircle } from '../ui/ErrorCircle';
import { ButtonV2 } from '../ui/PayButton';

const PREFIX = 'PayError';

const classes = {
    title: `${PREFIX}title`,
    icon: `${PREFIX}icon`,
    buttonContainer: `${PREFIX}buttonContainer`
};

const StyledBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    paddingTop: theme.spacing(2),
    [`& .${classes.title}`]: {
        fontSize: '24px',
        fontWeight: 500,
        color: theme.palette.text.primary
    },

    [`& .${classes.icon}`]: {
        height: 100,
        width: 100
    },

    [`& .${classes.buttonContainer}`]: {
        position: 'fixed',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: PAY_MAX_WIDTH,
        right: 0,
        padding: theme.spacing(1)
    }
}));

interface IProps {
    errorMessage?: string | null;
    showRetry?: boolean;
    lastRequest?: () => void;
    handleRetry?: () => void;
    payInstead?: number;
    onPayInstead?: () => void;
    extraTips?: number;
    onPayExtraTips?: () => void;
    errorCode?: string;
}

export const PayError: React.FC<React.PropsWithChildren<IProps>> = ({
    errorMessage,
    showRetry,
    handleRetry,
    lastRequest,
    payInstead,
    onPayInstead,
    extraTips,
    onPayExtraTips
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const getCurrencyString = useCurrencyString();

    return (
        <StyledBox>
            <Typography className={classes.title}>{t('PAT_QUICKPAY_ERROR_TITLE')}</Typography>
            {errorMessage && (
                <Box paddingX={1}>
                    <Typography align="center">{errorMessage}</Typography>
                </Box>
            )}
            <Box paddingTop={5}>
                <ErrorCircle className={classes.icon} color={theme.palette.primary.main} />
            </Box>
            {showRetry && lastRequest && (
                <div className={classes.buttonContainer}>
                    <ButtonV2 fullWidth onClick={handleRetry}>
                        {t('RETRY')}
                    </ButtonV2>
                </div>
            )}
            {isDefined(payInstead) && onPayInstead && (
                <div className={classes.buttonContainer}>
                    {isDefined(extraTips) && onPayExtraTips && (
                        <ButtonV2 fullWidth onClick={onPayExtraTips}>
                            {t('PAT_QUICKPAY_ERROR_OVERPAYMENT_EXTRA_TIP_BUTTON', {
                                tip: getCurrencyString(extraTips)
                            })}
                        </ButtonV2>
                    )}
                    <ButtonV2 fullWidth onClick={onPayInstead}>
                        {t('PAT_QUICKPAY_ERROR_OVERPAYMENT_BUTTON', {
                            amount: getCurrencyString(payInstead)
                        })}
                    </ButtonV2>
                </div>
            )}
        </StyledBox>
    );
};
